import React, { useMemo } from 'react'
import { ViewProps } from 'react-native'
import { object, string } from 'yup'
import { useTranslation } from 'react-i18next'
import { Draft, LocationData, useLocation } from 'zens-store'
import { NavigationConfirm, FormProps, AddressForm } from '@dms/component'
import { Href } from 'zens-router'

interface Props extends Omit<FormProps, 'draft' | 'config' | 'schema' | 'buttons'> {
  companyId: number
  locationId?: number
  onSave?: (draft: Draft<LocationData>) => Promise<void>
  onDelete?: (draft: Draft<LocationData>) => Promise<void>
  cancelHref?: Href
}
const LocationForm = (props: Props) => {
  const { companyId, locationId = 0, onSave, onDelete, cancelHref, ...otherProps } = props
  const location = useLocation({ companyId, locationId })
  const draft = useMemo(() => new Draft({ id: 0, company_id: companyId, ...location.data?.data }), [location.data])
  const { t } = useTranslation()

  const config = {
    fields: [
      {
        key: 'name',
        label: t('Location name'),
        help: t('Define the name of the location where you want to start adding workspaces and devices to.'),
        fieldProps: {
          testID: 'LocationForm.Name',
        },
      },
    ],
  }
  const schema = object({
    name: string().required(t('Location name is required')),
  })

  const buttons = [
    [
      {
        title: t('Save'),
        onPress: onSave,
        validate: true,
      },
      {
        title: t('Cancel'),
        href: cancelHref,
        light: true,
      },
    ],
    ...(onDelete
      ? [
          {
            title: t('Delete location'),
            onPress: onDelete,
            left: 'trash' as const,
            danger: true,
          },
        ]
      : []),
  ]

  return (
    <>
      <AddressForm draft={draft} config={config} schema={schema} buttons={buttons} {...otherProps} />
      <NavigationConfirm draft={draft} />
    </>
  )
}

interface SkeletonProps extends ViewProps {}

const Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  const { t } = useTranslation()
  const config = {
    fields: [
      {
        key: 'name',
        label: t('Location name'),
      },
    ],
  }
  return <AddressForm.Skeleton style={style} config={config} numberOfbuttons={[2, 1]} {...otherProps} />
}
LocationForm.Skeleton = Skeleton

export default LocationForm
